import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import {
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Slide,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  Grid,
  List,
  ListItem,
  Divider,
  Hidden,
  Box
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const content = {
  unlimited: (
    <DialogContentText id='alert-dialog-product-description-unlimited'>
      The Unlimited event plan function similar to a traditional policy. Subject
      to policy terms and conditions, all Named Storm Wind Events are covered
      during the policy term.
    </DialogContentText>
  ),
  single: (
    <DialogContentText id='alert-dialog-product-description-single'>
      Purchase a single <i>Named Storm Event Limit</i> at policy inception. An{' '}
      <i>Event Trigger</i> (Tropical Storm winds or a claim) will allocate one
      Event Limit to the Named Storm. Additional Named Storm Event Limits can be
      purchased, subject to a <i>Moratorium</i> (TS force winds at insured
      location). Annual premium will range from <b>85-140%</b> of the{' '}
      <b>Unlimited Plan</b>.
    </DialogContentText>
  ),
  zero: (
    <DialogContentText id='alert-dialog-product-description-zero'>
      Initial Policy premium gets your Location on the books. Buyer elects when
      to purchase coverage during any <i>Named Storm</i> (subject to Moratorium
      - TS force winds at insured location). A purchase for each specific Named
      Storm is required for Event coverage to be in effect. Annual premium will
      range from <b>65-200%</b> of the <b>Unlimited Plan</b>.
    </DialogContentText>
  )
};

const useAccordionStyles = makeStyles((theme) => ({
  root: {
    width: '100%'
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 2,
    color: theme.palette.text.secondary
  },
  accordionDetails: {
    maxHeight: 300,
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '5px',
      backgroundColor: 'rgba(0, 0, 0, 0)',
      WebkitBorderRadius: '100px'
    },
    '&::-webkit-scrollbar-hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.09)'
    },
    '&::-webkit-scrollbar-thumb:vertical': {
      background: '#dadada',
      WebkitBorderRadius: '100px'
    },
    '&::-webkit-scrollbar-thumb:vertical:active': {
      background: 'rgba(0, 0, 0, 0.6)',
      WebkitBorderRadius: '100px'
    }
  },
  accordionDetailWrapper: {
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2)
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3)
    }
  },
  negMargin: {
    [theme.breakpoints.up('md')]: {
      margin: '-1px'
    }
  }
}));

const AccordionComponent = () => {
  const classes = useAccordionStyles();
  const [expanded, setExpanded] = useState(false);

  const handleAccordion = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <div className={classes.root}>
      <Accordion
        expanded={expanded === 'panel1'}
        onChange={handleAccordion('panel1')}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls='panel1bh-content'
          id='panel1bh-header'
        >
          <Typography className={classes.heading}>Purchase Option</Typography>
          <Typography className={classes.secondaryHeading} noWrap>
            Manual vs. Automatic purchase of Event Limits
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <Grid container justify='space-between' alignItems='flex-start'>
            <Grid
              item
              xs={12}
              md={6}
              className={clsx(
                classes.accordionDetailWrapper,
                classes.negMargin
              )}
            >
              <Typography variant='h6' align='center' gutterBottom>
                Manual
              </Typography>
              <Typography variant='body2' color='textSecondary' align='justify'>
                You manage the policy and determine if/when you wish to purchase
                a new <i>Named Storm Event Limit</i>. Purchase is available up
                to the time of an <i>Event Trigger/Moratorium</i>.
              </Typography>
            </Grid>
            <Hidden smDown>
              <Divider orientation='vertical' flexItem />
            </Hidden>
            <Grid
              item
              xs={12}
              md={6}
              className={clsx(
                classes.accordionDetailWrapper,
                classes.negMargin
              )}
            >
              <Typography variant='h6' align='center' gutterBottom>
                Automatic
              </Typography>
              <Typography variant='body2' color='textSecondary' align='justify'>
                We fully manage the purchase of any Event Limit based on an{' '}
                <i>Event Trigger</i>. No action needed by the policyholder.
              </Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === 'panel2'}
        onChange={handleAccordion('panel2')}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls='panel2bh-content'
          id='panel2bh-header'
        >
          <Typography className={classes.heading}>Event Trigger</Typography>
          <Typography className={classes.secondaryHeading} noWrap>
            Wind Trigger vs. Event Trigger
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <Grid container>
            <Grid
              item
              xs={12}
              md={6}
              className={clsx(
                classes.accordionDetailWrapper,
                classes.negMargin
              )}
            >
              <Typography variant='h6' align='center' gutterBottom>
                Wind Claim Trigger
              </Typography>
              <Typography variant='body2' color='textSecondary' align='justify'>
                The date/time when the National Hurricane Center (NHC) forecast
                first reports Tropical Storm force wind speeds at your Risk
                Location. A Wind Trigger triggers three things at the respective
                Risk Location:
              </Typography>
              <List>
                <ListItem alignItems='flex-start'>
                  <Typography variant='body2' color='textSecondary'>
                    An Inventory Event Limit is allocated to the Named Storm; or
                  </Typography>
                </ListItem>
                <ListItem alignItems='flex-start'>
                  <Typography variant='body2' color='textSecondary'>
                    A Named Storm Single Event Limit is endorsed to a policy
                    with the Automatic Option plan; and
                  </Typography>
                </ListItem>
                <ListItem alignItems='flex-start'>
                  <Typography variant='body2' color='textSecondary'>
                    A Moratorium is now active restricting any purchase under a
                    Manual Option plan;
                  </Typography>
                </ListItem>
              </List>
            </Grid>
            <Hidden smDown>
              <Divider orientation='vertical' flexItem />
            </Hidden>
            <Grid
              item
              xs={12}
              md={6}
              className={clsx(
                classes.accordionDetailWrapper,
                classes.negMargin
              )}
            >
              <Typography variant='h6' align='center' gutterBottom>
                Event Claim Trigger
              </Typography>
              <Typography variant='body2' color='textSecondary' align='justify'>
                A claim filed for loss incurred from a Named Storm covered by
                your policy with an Inventory Event Limit available or, in the
                absence of a Wind Trigger on an Automatic Option plan with no
                Inventory Event Limit available at the time of the Named Storm,
                a Claim Trigger will trigger an Automatic Plan purchase of a
                Named Storm Single Event Limit.
              </Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === 'panel3'}
        onChange={handleAccordion('panel3')}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls='panel3bh-content'
          id='panel3bh-header'
        >
          <Typography className={classes.heading}>Moratorium</Typography>
          <Typography className={classes.secondaryHeading} noWrap>
            When Event Limit purchases close
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <Typography variant='body2' color='textSecondary' align='justify'>
            A moratorium is put in place when Tropical Storm force winds occur
            at the insured location. This is the date/time when you can no
            longer purchase new coverage for a specific Named Storm. Applies to
            the <i>manual purchase</i> option with Single or Zero policy plans.
          </Typography>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`scrollable-auto-tabpanel-${index}`}
      aria-labelledby={`scrollable-auto-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired
};

function a11yProps(index) {
  return {
    id: `scrollable-auto-tab-${index}`,
    'aria-controls': `scrollable-auto-tabpanel-${index}`
  };
}

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction='up' ref={ref} {...props} />;
});

const useExplainerStyles = makeStyles((theme) => ({
  dialogContent: {
    '&::-webkit-scrollbar': {
      width: '5px',
      backgroundColor: 'rgba(0, 0, 0, 0)',
      WebkitBorderRadius: '100px'
    },
    '&::-webkit-scrollbar-hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.09)'
    },
    '&::-webkit-scrollbar-thumb:vertical': {
      background: '#dadada',
      WebkitBorderRadius: '100px'
    },
    '&::-webkit-scrollbar-thumb:vertical:active': {
      background: 'rgba(0, 0, 0, 0.6)',
      WebkitBorderRadius: '100px'
    }
  }
}));

const ProductExplainer = ({ show, setShow }) => {
  const classes = useExplainerStyles();
  const [tabValue, setTabValue] = useState(1);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleClose = () => {
    setShow(false);
  };

  return (
    <div>
      <Dialog
        open={show}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        scroll={'paper'}
        aria-labelledby='alert-dialog-product-title'
        aria-describedby='alert-dialog-product-description'
      >
        <DialogTitle id='alert-dialogproducte-title'>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor='primary'
            textColor='primary'
            centered
          >
            <Tab label='Unlimited' {...a11yProps(0)} />
            <Tab label='Single' {...a11yProps(1)} />
            <Tab label='Zero' {...a11yProps(2)} />
          </Tabs>
        </DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          <TabPanel value={tabValue} index={0}>
            {content.unlimited}
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            {content.single}
            <AccordionComponent />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            {content.zero}
            <AccordionComponent />
          </TabPanel>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color='primary'>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

ProductExplainer.propTypes = {
  show: PropTypes.bool.isRequired,
  setShow: PropTypes.func.isRequired
};

export default ProductExplainer;
